import { Routes } from '@angular/router'

import { Action, Resource } from '@ti/contracts'
import { loggedInGuard, notLoggedInGuard } from '@ti/core/auth/guards'
import { allowOnEnv, checkAccess, smartRedirect } from '@ti/core/common/guards'
import { NotFoundPageComponent } from '@ti/core/ui-kit/layout/components/not-found-page'
import { provideAssistant } from '@ti/shared/assistant/assistant.providers'
import { NewPasswordComponent } from '@ti/shared/auth-ui/components'

import { AccountsPageComponent } from './account/components/accounts-page.component'
import { AccountsListComponent } from './account/components/list/accounts-list.component'
import { AdminUsersPageComponent } from './admin-users/components/users-page.component'
import { AdminAuditWrapperComponent } from './audit'
import { BillingAccountsListComponent } from './billing/components/billing-accounts/billing-accounts-list.component'
import { BillingHistoryListComponent } from './billing/components/billing-history-list/billing-history-list.component'
import { AdminBillingPageComponent } from './billing/components/billing-page.component'
import { BillingUsageReportComponent } from './billing/components/billing-usage-report/billing-usage-report.component'
import { TiAdminSignInPageComponent } from './common/components/auth/sign-in/sign-in-page.component'
import { TiAdminLayoutComponent } from './common/components/layout/layout.component'
import { mcpAuthorizeGuard } from './mcp/authorize/mcp-authorize.guard'
import { TSPUsersPageComponent } from './tsp-users/components/users-page.component'

const APP_ROUTES: Routes = [
  {
    path: '',
    component: TiAdminLayoutComponent,
    canActivate: [loggedInGuard],
    children: [
      {
        path: 'tsp-users',
        component: TSPUsersPageComponent,
        canActivate: [checkAccess(Resource.TSPUser, Action.View)],
      },
      {
        path: 'accounts',
        component: AccountsPageComponent,
        children: [
          {
            path: '',
            canActivate: [checkAccess(Resource.TSP, Action.View)],
            pathMatch: 'full',
            component: AccountsListComponent,
          },
        ],
      },
      {
        path: 'billing',
        canActivate: [checkAccess(Resource.Billing, Action.View)],
        component: AdminBillingPageComponent,
        children: [
          {
            path: '',
            canActivate: [checkAccess(Resource.Billing, Action.View)],
            component: BillingAccountsListComponent,
          },
          {
            path: ':tspId',
            canActivate: [checkAccess(Resource.Billing, Action.View)],
            component: BillingHistoryListComponent,
          },
          {
            path: ':tspId/:year/:month',
            canActivate: [checkAccess(Resource.Billing, Action.View)],
            component: BillingUsageReportComponent,
          },
          { path: '**', redirectTo: 'accounts' },
        ],
      },
      {
        path: 'admin-users',
        component: AdminUsersPageComponent,
        canActivate: [checkAccess(Resource.User, Action.Create)],
      },
      {
        path: 'logs',
        component: AdminAuditWrapperComponent,
        canActivate: [checkAccess(Resource.AuditLog, Action.View)],
      },
      {
        path: 'monitoring',
        loadChildren: () => import('./monitoring/monitoring.routes'),
      },
      {
        path: 'system-controls',
        loadChildren: () => import('./system-controls/system-controls.routes'),
      },
      {
        path: 'ai-support/memory',
        canActivate: [checkAccess(Resource.TSPUser, Action.LoginAs)],
        loadComponent: () =>
          import('./ai-memory/components/memory-page.component').then((m) => m.MemoryPageComponent),
      },
      {
        path: 'ai-support',
        loadChildren: () => import('@ti/shared/assistant/assistant.routes'),
        // TI has no chat feature of its own, so the realtime listener and the
        // markdown renderer the AI replies need are provided here rather than
        // application-wide -- nothing is loaded until the page is opened.
        providers: [
          provideAssistant({
            routeBase: '/ai-support',
            attachmentsOnProduction: true,
            teamThreads: true,
            memoryUrl: '/ai-support/memory',
          }),
        ],
      },
      {
        path: '',
        canActivate: [
          smartRedirect(
            { accounts: [Resource.TSP, Action.View], 'tsp-users': [Resource.TSPUser, Action.View] },
            'accounts',
          ),
        ],
        pathMatch: 'full',
        component: NotFoundPageComponent,
      },
    ],
  },
  { path: 'sign-in', canActivate: [notLoggedInGuard], component: TiAdminSignInPageComponent },
  { path: 'change-password', component: NewPasswordComponent },
  { path: 'create-password/:secret', component: NewPasswordComponent },
  { path: 'reset-password/:secret', component: NewPasswordComponent },
  // Outside the layout and deliberately not behind loggedInGuard: see mcpAuthorizeGuard.
  {
    path: 'mcp/authorize',
    canActivate: [mcpAuthorizeGuard],
    loadComponent: () =>
      import('./mcp/authorize/mcp-authorize.page').then((m) => m.McpAuthorizePageComponent),
  },

  {
    path: '___design-system',
    canActivate: [loggedInGuard],
    canMatch: [allowOnEnv(['local', 'dev'])],
    loadComponent: () =>
      import('@ti/core/design-system/design-system.component').then((m) => m.DesignSystemComponent),
  },

  {
    path: 'access-denied',
    loadComponent: () =>
      import('@ti/core/ui-kit/layout/components/access-denied-page/access-denied-page.component').then(
        (m) => m.AccessDeniedPageComponent,
      ),
  },
  {
    path: '**',
    canActivate: [
      smartRedirect(
        { 'tsp-users': [Resource.TSPUser, Action.View], accounts: [Resource.TSP, Action.View] },
        'tsp-users',
      ),
    ],
    component: NotFoundPageComponent,
  },
]

export default APP_ROUTES
